import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, X } from 'lucide-react';
import { Button, Chip, Spinner } from '@/components/ui';
import { apiBlob, apiData } from '@/lib/api';

/**
 * 인스타 편집기 (§5.5) — 교직원 전용.
 * 앨범 사진을 인스타 규격(4:5)으로 가운데 잘라 주고, 기울기 보정 슬라이더와
 * 옅은 필터, 원에서 등록한 프레임(원 소개에서 관리)을 입혀 파일로 저장한다.
 * 모든 처리는 이 기기 안에서만 일어난다 — 사진이 밖으로 나가지 않는다.
 */

const OUT_W = 1080;
const OUT_H = 1350; // 4:5

const FILTERS = [
  { code: 'none', label: '원본', css: 'none' },
  { code: 'warm', label: '포근', css: 'brightness(1.04) saturate(1.1) sepia(0.09)' },
  { code: 'clear', label: '맑음', css: 'brightness(1.06) contrast(1.04) saturate(1.05)' },
  { code: 'film', label: '필름', css: 'contrast(0.96) saturate(0.9) sepia(0.14) brightness(1.03)' },
] as const;

type FilterCode = (typeof FILTERS)[number]['code'];

/**
 * 원본 바이트는 API가 스트림해 준다(GET /media/:id/raw) —
 * R2 서명 URL을 fetch로 받으면 CORS에 막혀 캔버스에 그릴 수 없다.
 */
async function loadBitmap(mediaId: string): Promise<ImageBitmap> {
  return createImageBitmap(await apiBlob(`/media/${mediaId}/raw`));
}

export function InstaEditor({ mediaId, onClose }: { mediaId: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [photo, setPhoto] = useState<ImageBitmap | null>(null);
  const [frame, setFrame] = useState<ImageBitmap | null>(null);
  const [rotation, setRotation] = useState(0);
  const [filter, setFilter] = useState<FilterCode>('warm');
  const [useFrame, setUseFrame] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const intro = useQuery({
    queryKey: ['facility', 'intro'],
    queryFn: () => apiData<{ frameMediaId: string | null }>('/facilities/current/intro'),
  });
  const frameMediaId = intro.data?.frameMediaId ?? null;

  useEffect(() => {
    loadBitmap(mediaId)
      .then(setPhoto)
      .catch(() => setError('사진을 불러오지 못했어요. 잠시 후 다시 열어 주세요.'));
  }, [mediaId]);

  useEffect(() => {
    if (!frameMediaId) return;
    loadBitmap(frameMediaId).then(setFrame).catch(() => setFrame(null));
  }, [frameMediaId]);

  /** 4:5 캔버스에 회전 → 덮기(cover) 배치 → 필터 → 프레임 순서로 그린다 */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !photo) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const radians = (rotation * Math.PI) / 180;
    // 회전해도 모서리가 비지 않도록 여유 배율을 준다
    const safety = Math.abs(Math.sin(radians)) + Math.abs(Math.cos(radians));
    const scale = Math.max(OUT_W / photo.width, OUT_H / photo.height) * safety;

    ctx.save();
    ctx.clearRect(0, 0, OUT_W, OUT_H);
    ctx.filter = FILTERS.find((f) => f.code === filter)?.css ?? 'none';
    ctx.translate(OUT_W / 2, OUT_H / 2);
    ctx.rotate(radians);
    ctx.drawImage(
      photo,
      (-photo.width * scale) / 2,
      (-photo.height * scale) / 2,
      photo.width * scale,
      photo.height * scale,
    );
    ctx.restore();

    if (useFrame && frame) {
      ctx.filter = 'none';
      ctx.drawImage(frame, 0, 0, OUT_W, OUT_H);
    }
  }, [photo, frame, rotation, filter, useFrame]);

  useEffect(() => {
    draw();
  }, [draw]);

  const save = () => {
    canvasRef.current?.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'saesak-insta.jpg';
        anchor.click();
        URL.revokeObjectURL(url);
      },
      'image/jpeg',
      0.92,
    );
  };

  return (
    // bg-ink/90 같은 토큰+투명도 조합은 CSS 변수라 적용되지 않는다(뒤 화면이 비쳐 보이던 버그).
    // 명시적 rgba로 완전한 전체 화면 편집기를 만든다.
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto p-4 pb-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+16px)]"
      style={{ backgroundColor: 'rgba(30, 27, 22, 0.97)' }}
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="font-semibold text-white">인스타용 만들기</p>
        <button
          type="button"
          aria-label="닫기"
          className="tap grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>

      <div className="relative mx-auto w-full max-w-xs flex-1 overflow-hidden">
        {!photo && !error && <Spinner />}
        {error && <p className="rounded-sm bg-notice/20 px-3 py-2 text-sm text-white">{error}</p>}
        <canvas
          ref={canvasRef}
          width={OUT_W}
          height={OUT_H}
          className="mx-auto max-h-full w-full rounded-md object-contain"
        />
        {/* 수평 안내선 — 회전 조정할 때 기준이 된다 */}
        {photo && rotation !== 0 && (
          <div className="pointer-events-none absolute inset-0 grid grid-rows-3 opacity-40">
            <div className="border-b border-white" />
            <div className="border-b border-white" />
            <div />
          </div>
        )}
      </div>

      <div className="mx-auto mt-3 flex w-full max-w-xs flex-col gap-3">
        <label className="flex items-center gap-2 text-sm text-white">
          기울기
          <input
            type="range"
            min={-10}
            max={10}
            step={0.5}
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            className="min-w-0 flex-1 accent-white"
          />
          <span className="tnum w-10 text-right">{rotation}°</span>
        </label>

        <div className="scroll-x flex gap-1.5 overflow-x-auto">
          {FILTERS.map((item) => (
            <Chip
              key={item.code}
              tone={filter === item.code ? 'primary' : 'neutral'}
              selected={filter === item.code}
              onClick={() => setFilter(item.code)}
            >
              {item.label}
            </Chip>
          ))}
          {frame && (
            <Chip
              tone={useFrame ? 'primary' : 'neutral'}
              selected={useFrame}
              onClick={() => setUseFrame((prev) => !prev)}
            >
              원 프레임
            </Chip>
          )}
        </div>

        <Button block icon={<Download size={16} />} disabled={!photo} onClick={save}>
          4:5로 저장 (1080×1350)
        </Button>
        <p className="text-center text-xs text-white/70">
          보정은 이 기기 안에서만 처리돼요. 사진 공개에 동의하지 않은 아이가 담기지 않았는지
          올리기 전에 한 번 더 확인해 주세요.
        </p>
      </div>
    </div>
  );
}
